/**
 * ลงวันหยุดเอง (self-service day-off) — พนักงานเลือกวันหยุดในรอบ 26→25.
 * เปิดให้ลงวันที่ 20–25, ล็อกวันที่ 26. เพดานคนหยุดพร้อมกันต่อวันคุมโดย backend.
 */
import { useState, useMemo } from 'react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyDayoff, useBookDayoff } from '../hooks';
import { useToast } from '../components/ui/toast';
import { CalendarCheck, Info, Lock, Briefcase } from 'lucide-react';

const TH_MON = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const DOW = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

function fmtThai(dateStr: string): string {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${d} ${TH_MON[(m ?? 1) - 1]}`;
}
function dowUtc(dateStr: string): number {
  return new Date(`${dateStr}T00:00:00Z`).getUTCDay(); // 0=อา
}
function eachDate(from: string, to: string): string[] {
  const out: string[] = [];
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  for (let t = a.getTime(); t <= b.getTime(); t += 86400000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

export function MyDayoff() {
  const { data, isLoading } = useMyDayoff();
  const book = useBookDayoff();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [holWork, setHolWork] = useState<Set<string> | null>(null);

  // seed จาก my_dates ครั้งแรกที่โหลดเสร็จ — คัดวันนักขัตฯ ออก (หยุดอยู่แล้ว ไม่นับเป็นวันหยุดที่จอง)
  //   กันเคสวันที่จองไว้ก่อนแล้วภายหลังกลายเป็นวันนักขัตฯ ค้างในเซ็ต ถอดเองไม่ได้ + ทำให้ยอด/บันทึกเพี้ยน
  const holDates = new Set((data?.holidays ?? []).map((h) => h.date));
  const sel = selected ?? new Set((data?.my_dates ?? []).filter((d) => !holDates.has(d)));
  // วันนักขัตฤกษ์ที่กด "มาทำงาน" (default = หยุด ทุกวัน)
  const hw = holWork ?? new Set(data?.my_holiday_work_dates ?? []);

  const days = useMemo(
    () => (data ? eachDate(data.window.cycle_start, data.window.cycle_end) : []),
    [data?.window.cycle_start, data?.window.cycle_end],
  );

  if (isLoading || !data) {
    return <PageLayout title="ลงวันหยุด"><p className="py-8 text-center text-gray-500">กำลังโหลด…</p></PageLayout>;
  }

  const { window: w, quota, position } = data;
  const editable = w.is_open && !data.locked && quota != null;
  const cap = position?.cap ?? 0;
  const remaining = quota != null ? quota - sel.size : 0;

  // ชื่อเพื่อนร่วมตำแหน่งที่หยุดในแต่ละวัน (นโยบาย HR — ให้เห็นชื่อ)
  const namesByDay = new Map<string, string[]>();
  for (const t of data.teammates ?? []) {
    for (const d of t.dates) {
      const arr = namesByDay.get(d) ?? []; arr.push(t.name); namesByDay.set(d, arr);
    }
  }
  const teammateDays = [...namesByDay.keys()].sort();

  // วันหยุดนักขัตฤกษ์ในรอบ (แสดงบนปฏิทินให้เห็น) — { date: ชื่อวันหยุด }
  const holidayByDay = new Map<string, string>();
  for (const h of data.holidays ?? []) holidayByDay.set(h.date, h.name);

  // จำนวนคนอื่น (ไม่รวมของฉันที่บันทึกไว้) ที่ลงหยุดในแต่ละวัน → ใช้ตัดสิน "วันเต็ม"
  function othersCount(date: string): number {
    return (data!.day_counts[date] ?? 0) - (data!.my_dates.includes(date) ? 1 : 0);
  }
  function isFull(date: string): boolean {
    if (cap <= 0) return false;
    return othersCount(date) + 1 > cap;
  }

  function toggle(date: string) {
    if (!editable) return;
    // วันนักขัตฤกษ์ = หยุดอยู่แล้ว เลือกเป็นวันหยุดปกติไม่ได้ (ไปกด "มาทำงาน" ด้านล่างแทน)
    if (holidayByDay.has(date)) return;
    const next = new Set(sel);
    if (next.has(date)) {
      next.delete(date);
    } else {
      if (quota != null && next.size >= quota) { toast.error(`เลือกได้ไม่เกิน ${quota} วัน`); return; }
      if (isFull(date)) { toast.error(`วันนี้มีคนลงหยุดครบเพดาน ${cap} คนแล้ว`); return; }
      next.add(date);
    }
    setSelected(next);
  }

  // สลับ "มาทำงาน / หยุด" ของวันนักขัตฤกษ์ (default = หยุด)
  function toggleHolidayWork(date: string) {
    if (!editable) return;
    const next = new Set(hw);
    if (next.has(date)) next.delete(date); else next.add(date);
    setHolWork(next);
  }

  async function save() {
    try {
      await book.mutateAsync({ dates: [...sel].sort(), holiday_work_dates: [...hw].sort() });
      toast.success('บันทึกวันหยุดแล้ว');
      setSelected(null); setHolWork(null); // reseed จากข้อมูลใหม่
    } catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  const leadPad = days.length ? dowUtc(days[0]!) : 0;

  return (
    <PageLayout title="ลงวันหยุด">
      {/* สรุปหัว */}
      <div className="card p-4 mb-3">
        <div className="flex items-center gap-2 mb-1">
          <CalendarCheck className="w-5 h-5 text-brand-600" />
          <span className="font-semibold">รอบ {fmtThai(w.cycle_start)} – {fmtThai(w.cycle_end)}</span>
        </div>
        {quota == null ? (
          <div className="text-sm text-amber-700 bg-amber-50 rounded-lg p-2 mt-1">
            ยังไม่ได้ตั้งโควตาวันหยุดของแผนกคุณ — แจ้ง HR
          </div>
        ) : (
          <div className="text-sm text-gray-600">
            โควตา <b>{quota}</b> วัน/รอบ · เลือกแล้ว <b>{sel.size}</b> · เหลือ <b className={remaining < 0 ? 'text-red-600' : 'text-green-700'}>{remaining}</b>
            {position && (
              <span className="ml-1">
                · {position.name}{cap > 0 ? ` · เพดาน ${cap} คน/วัน` : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* สถานะหน้าต่างจอง */}
      {!w.is_open && (
        <div className="card p-3 mb-3 bg-blue-50 border-blue-200 flex items-start gap-2 text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {data.enabled
              ? `ยังไม่เปิดให้ลงวันหยุด — เปิดวันที่ ${w.open_day}–${w.lock_day - 1} ของเดือน (ตอนนี้ดูได้อย่างเดียว)`
              : 'ระบบลงวันหยุดปิดอยู่'}
          </span>
        </div>
      )}
      {data.locked && (
        <div className="card p-3 mb-3 bg-gray-50 flex items-center gap-2 text-sm text-gray-600">
          <Lock className="w-4 h-4" /> รอบนี้ถูกล็อกแล้ว แก้ไขไม่ได้
        </div>
      )}

      {/* ปฏิทิน */}
      <div className="card p-3">
        <div className="grid grid-cols-7 gap-1 mb-1 text-center text-[11px] text-gray-400">
          {DOW.map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: leadPad }).map((_, i) => <div key={`pad${i}`} />)}
          {days.map((date) => {
            const picked = sel.has(date);
            const full = isFull(date) && !picked;
            const cnt = data.day_counts[date] ?? 0;
            const holiday = holidayByDay.get(date);
            const working = holiday != null && hw.has(date); // นักขัตฯ + กดมาทำงาน
            const [, , dd] = date.split('-');
            return (
              <button
                key={date}
                onClick={() => (holiday ? toggleHolidayWork(date) : toggle(date))}
                disabled={!editable || (full && !holiday)}
                title={holiday
                  ? `วันหยุดนักขัตฤกษ์: ${holiday} — แตะเพื่อ${working ? 'ยกเลิกมาทำงาน (กลับเป็นหยุด)' : 'มาทำงาน (จ่าย 2 เท่า)'}`
                  : undefined}
                className={[
                  'aspect-square rounded-lg text-sm flex flex-col items-center justify-center relative transition',
                  working ? 'bg-emerald-600 text-white font-semibold'
                    : picked ? 'bg-brand-600 text-white font-semibold'
                    : full ? 'bg-red-50 text-red-300 cursor-not-allowed'
                    : holiday ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                    : editable ? 'bg-gray-50 hover:bg-brand-50 text-gray-700'
                    : 'bg-gray-50 text-gray-400',
                ].join(' ')}
              >
                <span>{Number(dd)}</span>
                {working ? (
                  <Briefcase className="w-3 h-3 text-white/90" />
                ) : cap > 0 && !holiday ? (
                  <span className={`text-[9px] leading-none ${picked ? 'text-white/80' : full ? 'text-red-400' : 'text-gray-400'}`}>
                    {cnt}/{cap}
                  </span>
                ) : null}
                {/* วันหยุดนักขัตฤกษ์ = จุดม่วงมุมซ้ายบน */}
                {holiday && !working && (
                  <span className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full bg-violet-500" />
                )}
                {namesByDay.has(date) && (
                  <span className={`absolute top-1 right-1 w-1.5 h-1.5 rounded-full ${picked ? 'bg-white/80' : 'bg-amber-400'}`}
                    title={`เพื่อนหยุด: ${namesByDay.get(date)!.join(', ')}`} />
                )}
              </button>
            );
          })}
        </div>

        {/* คำอธิบายสัญลักษณ์ */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" /> วันหยุดนักขัตฤกษ์ (หยุดอยู่แล้ว)</span>
          <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3 text-emerald-600" /> เลือกมาทำงาน (2 เท่า)</span>
          <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> เพื่อนร่วมตำแหน่งหยุด</span>
        </div>
      </div>

      {/* วันหยุดนักขัตฤกษ์ในรอบ — เลือก "มาทำงาน" รายวัน (ค่าเริ่มต้น = หยุด ไม่นับโควตา) */}
      {(data.holidays?.length ?? 0) > 0 && (
        <div className="card p-4 mt-4">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase className="w-4 h-4 text-emerald-600" />
            <h2 className="font-semibold text-sm">วันหยุดนักขัตฤกษ์ในรอบนี้</h2>
          </div>
          <p className="text-[12px] text-gray-500 mb-2">ค่าเริ่มต้น = หยุด (ได้เงินเดือนปกติ ไม่นับโควตา) · กด “มาทำงาน” ถ้าจะเข้างานวันนั้น → มาสแกนจริงจ่าย <b>2 เท่า</b></p>
          <ul className="space-y-2">
            {data.holidays!.map((h) => {
              const working = hw.has(h.date);
              return (
                <li key={h.date} className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800">{fmtThai(h.date)}</div>
                    <div className="text-[12px] text-gray-500 truncate">{h.name || 'วันหยุดนักขัตฤกษ์'}</div>
                  </div>
                  <button
                    onClick={() => toggleHolidayWork(h.date)}
                    disabled={!editable}
                    className={[
                      'shrink-0 px-3 py-1.5 rounded-full text-[13px] font-medium transition',
                      working ? 'bg-emerald-600 text-white'
                        : editable ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        : 'bg-gray-100 text-gray-400',
                    ].join(' ')}
                  >
                    {working ? '✓ มาทำงาน (2 เท่า)' : 'หยุด'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {editable && (
        <button onClick={save} disabled={book.isPending || remaining < 0}
          className="btn-primary w-full mt-4">
          {book.isPending ? 'กำลังบันทึก…' : `บันทึก (${sel.size} วัน)`}
        </button>
      )}

      {/* เพื่อนร่วมตำแหน่งที่หยุดในรอบนี้ (ให้ดูก่อนเลือก ไม่ให้ชนกัน) */}
      {teammateDays.length > 0 && (
        <div className="card p-4 mt-4">
          <h2 className="font-semibold text-sm mb-2">เพื่อนร่วมตำแหน่งที่หยุดในรอบนี้</h2>
          <ul className="space-y-1.5">
            {teammateDays.map((d) => {
              const [, mm, dd] = d.split('-');
              return (
                <li key={d} className="flex gap-2 text-sm">
                  <span className="shrink-0 w-12 font-medium text-brand-700 tabular-nums">{Number(dd)}/{Number(mm)}</span>
                  <span className="text-gray-600">{namesByDay.get(d)!.join(', ')}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </PageLayout>
  );
}
